/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useLabels } from '@/hooks/useLabels';
import { LoadingIndicatorDots } from '@/components/LoadingIndicatorDots';
import type { NearestStoresProps } from './types';
import styles from './NearestStores.module.scss';
import { useNearByStores } from '../../api/getNearByStoreDetails';
import type { YextStoreEntity } from '../../interface/yext-types';
import { StoreCard } from '../StoreCard/StoreCard';
import { Headline } from '@/components/Headline/Headline';
import { useStoreDetails } from '../../api/getStoreDetails';

const labelsMap = {
  error: 'label_locations_StoreLocator_error',
};

export function NearestStores({ title }: NearestStoresProps) {
  const labels = useLabels(labelsMap);
  const { data: currentStore } = useStoreDetails();
  const {
    data: storeDetails,
    isLoading,
    isError,
  } = useNearByStores({
    storeId: currentStore?.meta.id,
    searchParams: currentStore?.yextDisplayCoordinate
      ? {
          type: 'coordinates',
          latitude: currentStore.yextDisplayCoordinate.latitude,
          longitude: currentStore.yextDisplayCoordinate.longitude,
          radius: undefined,
          searchText: undefined,
        }
      : undefined,
    limit: 3,
  });
  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <LoadingIndicatorDots size={10} color="#000" />
      </div>
    );
  }
  if (isError) {
    return <div className={styles.errorContainer}>{labels.error}</div>;
  }
  const stores = storeDetails?.response.entities || [];

  return stores.length > 0 ? (
    <div className={styles.container}>
      {title && <Headline className={styles.title}>{title}</Headline>}
      <ul className={styles.storeList}>
        {stores.map((store: YextStoreEntity) => {
          return (
            <li key={store.meta.id} className={styles.storeListItem}>
              <StoreCard store={store} showActions={false} />
            </li>
          );
        })}
      </ul>
    </div>
  ) : null;
}
